// LabelsNamesOffice.jsx
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    Grid,
    Heading,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import html2pdf from "html2pdf.js";
import { useEffect, useMemo, useRef, useState } from "react";

// --------------------- helpers ---------------------
const uid = () => {
    try {
        return crypto.randomUUID();
    } catch (_) {
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
};

const clampInt = (v, min, max) => {
    const n = Number.isFinite(+v) ? Math.trunc(+v) : min;
    return Math.max(min, Math.min(max, n));
};

const titleCase = (s) => {
    const t = (s || "").trim();
    if (!t) return "";
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

const upper = (s) => (s || "").trim().toUpperCase();

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const normalize2DigitYear = (yy) => {
    const n = clampInt(yy, 0, 99);
    return n <= 29 ? 2000 + n : 1900 + n;
};

const formatDOB = (value) => {
    const raw0 = (value || "").trim();
    if (!raw0) return "";

    const raw = raw0.replace(/\s+/g, " ").trim();

    const m = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2}|\d{4})$/);
    if (m) {
        const mm = clampInt(m[1], 1, 12);
        const dd = clampInt(m[2], 1, 31);
        const y = m[3].length === 2 ? normalize2DigitYear(m[3]) : clampInt(m[3], 1900, 2100);
        return `${String(mm).padStart(2, "0")}/${String(dd).padStart(2, "0")}/${String(y)}`;
    }

    const cleaned = onlyDigits(raw);
    if (cleaned.length === 8) {
        const mm = clampInt(cleaned.slice(0, 2), 1, 12);
        const dd = clampInt(cleaned.slice(2, 4), 1, 31);
        const yyyy = cleaned.slice(4);
        return `${String(mm).padStart(2, "0")}/${String(dd).padStart(2, "0")}/${yyyy}`;
    }

    if (cleaned.length === 6) {
        const mm = clampInt(cleaned.slice(0, 2), 1, 12);
        const dd = clampInt(cleaned.slice(2, 4), 1, 31);
        const yyyy = normalize2DigitYear(cleaned.slice(4));
        return `${String(mm).padStart(2, "0")}/${String(dd).padStart(2, "0")}/${yyyy}`;
    }

    return raw;
};

const isValidDOB = (mmddyyyy) => {
    if (!mmddyyyy) return true;
    const m = String(mmddyyyy).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const mm = +m[1];
    const dd = +m[2];
    const yyyy = +m[3];
    if (mm < 1 || mm > 12) return false;
    if (dd < 1 || dd > 31) return false;
    if (yyyy < 1900 || yyyy > 2100) return false;
    return true;
};

const normalizeKey = (p) =>
    `${(p.lastName || "").trim()}-${(p.firstName || "").trim()}-${(p.dob || "").trim()}`.toLowerCase();

const parseDelimitedText = (text) => {
    const t = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
    if (!t) return [];
    const lines = t.split("\n").filter((l) => l.trim().length);
    const isLikelyTSV = lines.some((l) => l.includes("\t"));
    if (isLikelyTSV) return lines.map((l) => l.split("\t").map((c) => c.trim()));

    const parseCsvLine = (line) => {
        const out = [];
        let cur = "";
        let inQ = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"' && line[i + 1] === '"') {
                cur += '"';
                i++;
                continue;
            }
            if (ch === '"') {
                inQ = !inQ;
                continue;
            }
            if (ch === "," && !inQ) {
                out.push(cur.trim());
                cur = "";
                continue;
            }
            cur += ch;
        }
        out.push(cur.trim());
        return out;
    };

    return lines.map(parseCsvLine);
};

const extractDobFromEnd = (s) => {
    const t0 = (s || "").trim();
    if (!t0) return { rest: "", dob: "" };

    const t = t0.replace(/[,\)\]]+$/g, "").trim();

    const m1 = t.match(/^(.*?)(\b\d{1,2}[\/-]\d{1,2}[\/-](?:\d{2}|\d{4})\b)\s*$/);
    if (m1) return { rest: (m1[1] || "").trim(), dob: formatDOB(m1[2]) };

    const m2 = t.match(/^(.*?)(\b\d{6}\b|\b\d{8}\b)\s*$/);
    if (m2) return { rest: (m2[1] || "").trim(), dob: formatDOB(m2[2]) };

    return { rest: t0, dob: "" };
};

const parseSingleColumnNameLine = (line) => {
    const raw = (line || "").trim();
    if (!raw) return { lastName: "", firstName: "", dob: "" };

    if (raw.includes(",")) {
        const parts = raw.split(",");
        const leftRaw = (parts[0] || "").trim(); // first
        const rightRaw = parts.slice(1).join(",").trim(); // last + dob
        const right = extractDobFromEnd(rightRaw);
        return { firstName: leftRaw, lastName: right.rest, dob: right.dob };
    }

    const { rest, dob } = extractDobFromEnd(raw);
    const tokens = rest.split(/\s+/).filter(Boolean);
    if (tokens.length === 1) return { lastName: tokens[0], firstName: "", dob };
    return { lastName: tokens[tokens.length - 1], firstName: tokens.slice(0, -1).join(" "), dob };
};

const toCsv = (rows) => {
    const esc = (v) => {
        const s = String(v ?? "");
        if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };
    return rows.map((r) => r.map(esc).join(",")).join("\n");
};

const downloadCsv = (filename, csvText) => {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// --------------------- component ---------------------
export default function LabelsNamesOffice() {
    const toast = useToast();

    const [rows, setRows] = useState(() =>
        Array.from({ length: 10 }).map(() => ({ id: uid(), lastName: "", firstName: "", dob: "" }))
    );

    const [nameStyle, setNameStyle] = useState("title"); // title | upperLast
    const [fontSize, setFontSize] = useState(12); // 9..18
    const [showAllRows, setShowAllRows] = useState(false);

    const howTo = useDisclosure();
    const pasteModal = useDisclosure();
    const [pasteText, setPasteText] = useState("");

    // scroll/highlight
    const [focusRowId, setFocusRowId] = useState(null);
    const rowRefs = useRef({}); // { [id]: HTMLTableRowElement | null }

    const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const deleteRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
    const addRow = () => setRows((prev) => [...prev, { id: uid(), lastName: "", firstName: "", dob: "" }]);
    const clearAll = () =>
        setRows(Array.from({ length: 10 }).map(() => ({ id: uid(), lastName: "", firstName: "", dob: "" })));

    const processed = useMemo(() => {
        const cleaned = rows
            .map((r) => {
                const last = (r.lastName || "").trim();
                const first = (r.firstName || "").trim();
                const dob = formatDOB(r.dob || "");
                const errors = [];
                if (!last) errors.push("Last name required");
                if (dob && !isValidDOB(dob)) errors.push("DOB invalid (MM/DD/YYYY)");
                return { id: r.id, lastName: last, firstName: first, dob, errors };
            })
            .filter((r) => r.lastName || r.firstName || r.dob);

        return cleaned.map((r) => {
            let lastName = r.lastName;
            let firstName = r.firstName;

            if (nameStyle === "title") {
                lastName = titleCase(lastName);
                firstName = titleCase(firstName);
            } else {
                lastName = upper(lastName);
                firstName = titleCase(firstName);
            }
            return { ...r, lastName, firstName };
        });
    }, [rows, nameStyle]);

    const duplicates = useMemo(() => {
        // key -> [ids...]
        const map = new Map();
        for (const p of processed) {
            if (!p.lastName) continue;
            if (p.errors?.length) continue; // errors handled separately
            const key = normalizeKey(p);
            const arr = map.get(key) || [];
            arr.push(p.id);
            map.set(key, arr);
        }
        // filter to only true duplicates
        const dupMap = new Map();
        for (const [k, ids] of map.entries()) {
            if (ids.length > 1) dupMap.set(k, ids);
        }
        return dupMap; // Map<string, string[]>
    }, [processed]);

    const dupIdSet = useMemo(() => {
        const s = new Set();
        for (const ids of duplicates.values()) ids.forEach((id) => s.add(id));
        return s; // Set<rowId>
    }, [duplicates]);

    const errorIdSet = useMemo(() => {
        const s = new Set();
        processed.forEach((p) => {
            if (p.errors?.length) s.add(p.id);
        });
        return s;
    }, [processed]);

    const errorCount = errorIdSet.size;
    const dupCount = duplicates.size;

    const ok = useMemo(() => processed.filter((p) => p.lastName && !(p.errors?.length)), [processed]);

    const labels = useMemo(() => ok, [ok]);
    const totalPages = useMemo(() => Math.ceil(labels.length / 30) || 1, [labels.length]);
    const pages = useMemo(() => Array.from({ length: totalPages }).map((_, i) => i), [totalPages]);
    const pageSlice = (pi) => labels.slice(pi * 30, pi * 30 + 30);

    const mapColsToRow = (cols) => {
        const c = (cols || []).map((x) => String(x ?? "").trim());
        const nonEmpty = c.filter((x) => x.length);
        if (!nonEmpty.length) return null;

        if (c.length === 1) return { id: uid(), ...parseSingleColumnNameLine(c[0]) };
        if (c.length === 2) return { id: uid(), ...parseSingleColumnNameLine(`${c[0]}, ${c[1]}`) };
        return { id: uid(), lastName: c[0] || "", firstName: c[1] || "", dob: c[2] || "" };
    };

    const handlePasteApply = () => {
        const parsed = parseDelimitedText(pasteText);
        const mapped = parsed.map(mapColsToRow).filter(Boolean);

        if (!mapped.length) {
            toast({ title: "Nothing to paste", status: "warning", duration: 1500, isClosable: true });
            return;
        }

        setRows((prev) => {
            const starterAllEmpty = prev.every((r) => !r.lastName && !r.firstName && !r.dob);
            return starterAllEmpty ? mapped : [...prev, ...mapped];
        });

        setPasteText("");
        pasteModal.onClose();
        toast({
            title: "Added",
            description: `Added ${mapped.length} row(s).`,
            status: "success",
            duration: 1800,
            isClosable: true,
        });
    };

    const handleImportCsv = async (file) => {
        if (!file) return;
        const text = await file.text();
        const parsed = parseDelimitedText(text);

        const maybeHeader = parsed[0] || [];
        const headerLike = maybeHeader.some((c) => /last|first|dob/i.test(String(c)));
        const body = headerLike ? parsed.slice(1) : parsed;

        const mapped = body.map(mapColsToRow).filter(Boolean);

        if (!mapped.length) {
            toast({ title: "No rows found", status: "warning", duration: 1500, isClosable: true });
            return;
        }

        setRows((prev) => {
            const starterAllEmpty = prev.every((r) => !r.lastName && !r.firstName && !r.dob);
            return starterAllEmpty ? mapped : [...prev, ...mapped];
        });

        toast({
            title: "Imported",
            description: `Imported ${mapped.length} row(s).`,
            status: "success",
            duration: 1800,
            isClosable: true,
        });
    };

    const generatePDF = async () => {
        if (!labels.length) {
            toast({ title: "Nothing to export", status: "warning", duration: 1500, isClosable: true });
            return;
        }

        try {
            await html2pdf()
                .set({
                    margin: 0,
                    filename: "labels.pdf",
                    html2canvas: { scale: 2 },
                    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
                })
                .from(document.getElementById("print-area"))
                .save();

            toast({ title: "PDF created", status: "success", duration: 1500, isClosable: true });
        } catch (e) {
            toast({
                title: "PDF failed",
                description: String(e?.message || e),
                status: "error",
                duration: 2500,
                isClosable: true,
            });
        }
    };

    const exportCsv = () => {
        const csv = toCsv([["Last Name", "First Name", "DOB"], ...ok.map((p) => [p.lastName, p.firstName, p.dob])]);
        downloadCsv("labels_export.csv", csv);
    };

    const visibleRows = useMemo(() => (showAllRows ? rows : rows.slice(0, 15)), [rows, showAllRows]);
    const hiddenCount = rows.length > 15 ? rows.length - 15 : 0;

    const jumpToRow = (rowId) => {
        setShowAllRows(true);
        setFocusRowId(rowId);

        // wait for render to show all rows and ref attach
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = rowRefs.current?.[rowId];
                if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        });
    };

    // auto clear highlight after a bit
    useEffect(() => {
        if (!focusRowId) return;
        const t = setTimeout(() => setFocusRowId(null), 2500);
        return () => clearTimeout(t);
    }, [focusRowId]);

    return (
        <>
            <style>
                {`
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
            .print-page { page-break-after: always; }
          }
        `}
            </style>

            <Box p={6}>
                {/* UI */}
                <HStack justify="space-between" mb={4} className="no-print">
                    <Stack spacing={0}>
                        <Heading size="lg">Patient Labels</Heading>
                        <Text fontSize="sm" color={"gray.500"}>
                            Label spec: 2.625 in X 1 in (3 columns X 10 rows per letter page; 30 labels/page).
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            Add/import → review → download PDF
                        </Text>
                    </Stack>
                    <Button size="sm" variant="outline" onClick={howTo.onOpen}>
                        How To Use
                    </Button>
                </HStack>

                <Grid templateColumns={{ base: "1fr", lg: "1.25fr 0.75fr" }} gap={6}>
                    <Box borderWidth="1px" borderRadius="lg" p={4} className="no-print">
                        <Stack spacing={4}>
                            <HStack flexWrap="wrap" gap={2}>
                                <Button size="sm" colorScheme="blue" onClick={pasteModal.onOpen}>
                                    Add Names
                                </Button>

                                <label>
                                    <Input
                                        type="file"
                                        accept=".csv,text/csv"
                                        display="none"
                                        onChange={(e) => handleImportCsv(e.target.files?.[0])}
                                    />
                                    <Button as="span" size="sm" colorScheme="green">
                                        Import CSV
                                    </Button>
                                </label>

                                <Button size="sm" colorScheme="red" onClick={clearAll}>
                                    Clear
                                </Button>
                            </HStack>

                            <HStack flexWrap="wrap" gap={2}>
                                <Badge colorScheme="blue">{ok.length} ready</Badge>

                                {/* clickable error badge */}
                                <Tooltip label={errorCount ? "Click to jump to first error" : ""} isDisabled={!errorCount}>
                                    <Badge
                                        cursor={errorCount ? "pointer" : "default"}
                                        onClick={() => {
                                            if (!errorCount) return;
                                            const firstErr = processed.find((p) => p.errors?.length)?.id;
                                            if (firstErr) jumpToRow(firstErr);
                                        }}
                                        colorScheme={errorCount ? "red" : "green"}
                                    >
                                        {errorCount} errors
                                    </Badge>
                                </Tooltip>

                                {/* clickable dup badge */}
                                <Tooltip label={dupCount ? "Click to jump to first duplicate" : ""} isDisabled={!dupCount}>
                                    <Badge
                                        cursor={dupCount ? "pointer" : "default"}
                                        onClick={() => {
                                            if (!dupCount) return;
                                            const firstDupId = Array.from(duplicates.values())[0]?.[0];
                                            if (firstDupId) jumpToRow(firstDupId);
                                        }}
                                        colorScheme={dupCount ? "yellow" : "green"}
                                    >
                                        {dupCount} dup groups
                                    </Badge>
                                </Tooltip>

                                <Badge colorScheme="purple">{totalPages} page(s)</Badge>
                            </HStack>

                            {errorCount > 0 && (
                                <Alert status="error" borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <AlertTitle fontSize="sm">Fix errors before PDF</AlertTitle>
                                        <AlertDescription fontSize="sm">
                                            Click the red error badge to jump to the first error row.
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            <Box borderWidth="1px" borderRadius="md" overflowX="auto">
                                <Table size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th w="44px">#</Th>
                                            <Th>Last Name *</Th>
                                            <Th>First Name</Th>
                                            <Th>DOB</Th>
                                            <Th w="170px">Status</Th>
                                            <Th w="80px"></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {visibleRows.map((r, idx) => {
                                            const p = processed.find((x) => x.id === r.id);
                                            const errs = p?.errors || [];
                                            const isErr = !!errs.length;
                                            const isDup = dupIdSet.has(r.id);

                                            const isFocused = focusRowId === r.id;

                                            const statusBadge = isErr ? (
                                                <Badge colorScheme="red" cursor="pointer" onClick={() => jumpToRow(r.id)}>
                                                    Error
                                                </Badge>
                                            ) : r.lastName || r.firstName || r.dob ? (
                                                <Badge colorScheme="green">OK</Badge>
                                            ) : (
                                                <Badge>Empty</Badge>
                                            );

                                            const dupBadge = isDup && !isErr ? (
                                                <Badge colorScheme="yellow" cursor="pointer" onClick={() => jumpToRow(r.id)}>
                                                    Dup
                                                </Badge>
                                            ) : null;

                                            return (
                                                <Tr
                                                    key={r.id}
                                                    ref={(el) => {
                                                        rowRefs.current[r.id] = el;
                                                    }}
                                                    bg={isFocused ? "yellow.100" : "transparent"}
                                                    transition="background 0.2s"
                                                >
                                                    <Td color="gray.500">{idx + 1}</Td>

                                                    {/* highlight ONLY in table */}
                                                    <Td>
                                                        <Input
                                                            size="sm"
                                                            value={r.lastName}
                                                            onChange={(e) => updateRow(r.id, { lastName: e.target.value })}
                                                            placeholder="Last"
                                                            borderColor={isErr ? "red.400" : isDup ? "yellow.400" : undefined}
                                                            boxShadow={isFocused ? "0 0 0 1px rgba(49,130,206,0.35)" : undefined}
                                                        />
                                                    </Td>

                                                    <Td>
                                                        <Input
                                                            size="sm"
                                                            value={r.firstName}
                                                            onChange={(e) => updateRow(r.id, { firstName: e.target.value })}
                                                            placeholder="First"
                                                            borderColor={isErr ? "red.400" : isDup ? "yellow.400" : undefined}
                                                            boxShadow={isFocused ? "0 0 0 1px rgba(49,130,206,0.35)" : undefined}
                                                        />
                                                    </Td>

                                                    <Td>
                                                        <Input
                                                            size="sm"
                                                            value={r.dob}
                                                            onChange={(e) => updateRow(r.id, { dob: e.target.value })}
                                                            placeholder="MM/DD/YYYY"
                                                            borderColor={isErr ? "red.400" : undefined}
                                                            boxShadow={isFocused ? "0 0 0 1px rgba(49,130,206,0.35)" : undefined}
                                                        />
                                                    </Td>

                                                    <Td>
                                                        <HStack spacing={2}>
                                                            {statusBadge}
                                                            {dupBadge}
                                                            {isErr ? (
                                                                <Tooltip label={errs.join(" • ")}>
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="red.600"
                                                                        noOfLines={1}
                                                                        maxW="220px"
                                                                        cursor="pointer"
                                                                        onClick={() => jumpToRow(r.id)}
                                                                    >
                                                                        {errs[0]}
                                                                    </Text>
                                                                </Tooltip>
                                                            ) : null}
                                                        </HStack>
                                                    </Td>

                                                    <Td textAlign="right">
                                                        <Tooltip label="Delete row">
                                                            <IconButton
                                                                size="sm"
                                                                aria-label="Delete row"
                                                                icon={<span style={{ fontWeight: 700 }}>×</span>}
                                                                variant="ghost"
                                                                onClick={() => deleteRow(r.id)}
                                                            />
                                                        </Tooltip>
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </Box>

                            <Button size="sm" colorScheme="blue" onClick={addRow}>
                                Add row
                            </Button>

                            {hiddenCount > 0 && (
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="gray.600">
                                        Showing {showAllRows ? rows.length : 15} of {rows.length} rows
                                    </Text>
                                    <Button size="sm" variant="outline" onClick={() => setShowAllRows((v) => !v)}>
                                        {showAllRows ? "Show first 15" : `Show all (${rows.length})`}
                                    </Button>
                                </HStack>
                            )}
                        </Stack>
                    </Box>

                    <Box borderWidth="1px" borderRadius="lg" p={4} className="no-print">
                        <Stack spacing={4}>
                            <Text fontWeight="600">Options</Text>

                            <FormControl>
                                <FormLabel>Name casing</FormLabel>
                                <Select value={nameStyle} onChange={(e) => setNameStyle(e.target.value)}>
                                    <option value="title">Title Case</option>
                                    <option value="upperLast">UPPER last, Title first</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Font size</FormLabel>
                                <Select value={String(fontSize)} onChange={(e) => setFontSize(clampInt(e.target.value, 9, 18))}>
                                    {[9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((n) => (
                                        <option key={n} value={String(n)}>
                                            {n}px
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider />

                            <HStack>
                                <Button colorScheme="orange" w="full" onClick={generatePDF} isDisabled={!labels.length}>
                                    Download PDF
                                </Button>
                                <Button variant="outline" w="full" onClick={exportCsv} isDisabled={!ok.length}>
                                    Export CSV
                                </Button>
                            </HStack>

                            <Text fontSize="xs" color="gray.600">
                                Duplicates/errors are highlighted only in the table (not on labels).
                            </Text>
                        </Stack>
                    </Box>
                </Grid>

                {/* PRINT AREA: NO DUP/ERROR COLORING */}
                <Box mt={6}>
                    <Box id="print-area">
                        <Stack spacing={0}>
                            {pages.map((pi) => (
                                <Box
                                    key={pi}
                                    className="print-page"
                                    bg="white"
                                    width="8.5in"
                                    minH="11in"
                                    pt="0.5in"
                                    pl="0.1875in"
                                    mx="auto"
                                >
                                    <SimpleGrid columns={3} spacingX="0.125in">
                                        {pageSlice(pi).map((p, i) => {
                                            const line1 = `${p.lastName}${p.firstName ? "," : ""} ${p.firstName || ""}`.trim();
                                            const labelText = p?.dob ? `${line1}\n${p.dob}` : line1;

                                            return (
                                                <Box
                                                    key={`${p.id}-${i}`}
                                                    width="2.625in"
                                                    height="1in"
                                                    border="1px solid #ddd"
                                                    // borderRadius="15%"
                                                    mr="20px"
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    textAlign="center"
                                                    px={3}
                                                    fontSize={`${fontSize}px`}
                                                    lineHeight="1.2"
                                                    wordBreak="break-word"
                                                    bg="transparent"
                                                >
                                                    <Text fontWeight="600" whiteSpace="pre-line">
                                                        {labelText}
                                                    </Text>
                                                </Box>
                                            );
                                        })}

                                        {Array.from({ length: Math.max(0, 30 - pageSlice(pi).length) }).map((_, k) => (
                                            <Box
                                                key={`pad-${pi}-${k}`}
                                                width="2.625in"
                                                height="1in"
                                                border="1px solid #ddd"
                                                // borderRadius="15%"
                                                mr="20px"
                                                bg="transparent"
                                            />
                                        ))}
                                    </SimpleGrid>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            {/* How To Use */}
            <Modal isOpen={howTo.isOpen} onClose={howTo.onClose} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>How To Use</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={3}>
                            <Text>Add names, then download PDF.</Text>
                            <Divider />
                            <Text fontWeight="600">Paste formats supported</Text>
                            <Text fontSize="sm" color="gray.600">
                                Lines like: <b>john, doe 11/10/1980</b>
                            </Text>
                            <Text fontSize="sm" color="gray.600">DOB prints on a new line.</Text>
                            <Text fontSize="sm" color="gray.600">
                                Duplicate/error highlights are in the table only (click badges to jump).
                            </Text>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={howTo.onClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Paste Modal */}
            <Modal isOpen={pasteModal.isOpen} onClose={pasteModal.onClose} isCentered size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Paste</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Stack spacing={3}>
                            <Text fontSize="sm" color="gray.600">
                                Paste columns or lines like: <b>john, doe 11/10/1980</b>, each name one line
                            </Text>
                            <Textarea
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                                rows={10}
                                placeholder={`john, doe 11/10/1980\njane, doe 11/10/1985`}
                            />
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <HStack>
                            <Button variant="outline" onClick={pasteModal.onClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="blue" onClick={handlePasteApply}>
                                Apply
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
